import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';

const FeaturesContext = createContext();

const STORAGE_KEY = 'features';
const CACHE_VERSION_COOKIE = 'cv';

/**
 * Get cached features from localStorage if cache is valid (uses cv cookie, same as client)
 */
const getCachedFeatures = () => {
    try {
        const cached = localStorage.getItem(STORAGE_KEY);
        const cv = Cookies.get(CACHE_VERSION_COOKIE);
        if (cached && cv) {
            return JSON.parse(cached);
        }
    } catch (err) {
        console.warn('Failed to read cached features:', err);
    }
    return null;
};

/**
 * Save features to localStorage and set cv cache cookie (same as client)
 */
const setCachedFeatures = (data) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        Cookies.set(CACHE_VERSION_COOKIE, '1', { expires: 1 / 24 });
    } catch (err) {
        console.warn('Failed to cache features:', err);
    }
};

/**
 * Hook to access features throughout the application
 */
export const useFeatures = () => {
    const context = useContext(FeaturesContext);
    if (!context) {
        throw new Error('useFeatures must be used within FeaturesProvider');
    }
    return context;
};

/**
 * Provider component that fetches and provides features to the app
 * Uses localStorage caching to avoid redundant API calls
 */
export const FeaturesProvider = ({ children }) => {
    const cachedData = getCachedFeatures();
    const [features, setFeatures] = useState(cachedData);
    const [isLoading, setIsLoading] = useState(!cachedData);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (cachedData) {
            setIsLoading(false);
            return;
        }

        const fetchFeatures = async () => {
            try {
                const response = await fetch('/api/client/cookie/features?app=job-portal');
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const result = await response.json();
                if (result.success && result.data) {
                    setFeatures(result.data);
                    setCachedFeatures(result.data);
                }
            } catch (err) {
                console.error('Failed to fetch features:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchFeatures();
    }, [cachedData]);

    const featMap = features || {};
    const value = {
        features: featMap,
        isLoading,
        error,
        getFeatureByKey: (key) => (key in featMap) ? { key, value: featMap[key] } : undefined,
        hasFeature: (key) => featMap[key] === 1,
    };

    return (
        <FeaturesContext.Provider value={value}>
            {children}
        </FeaturesContext.Provider>
    );
};

export default FeaturesContext;
